/**
 * Sprint 23.5 — HireDraftEquippedWith.
 *
 * Multi-select over the workspace's MCP connections plus an "All" toggle, used
 * by the Hire wizard draft only. The draft value ("all" | string[]) is resolved
 * to explicit `agent_equipment` junction rows by HireWizardPage after the agent
 * is created (Sprint 24 — connection_scope was dropped from agent_config). AI
 * helper `recommend_equipped_tools` pre-fills based on role + bio.
 *
 * Empty state (no connections at all) shows a link to /settings/endpoints
 * — but that's just guidance text; the panel still renders cleanly so the
 * Hire flow isn't blocked.
 */

import { useMemo, type CSSProperties } from "react";
import { Link } from "react-router";
import { useConnections, type Connection } from "../../../hooks/use-connections";

export interface HireDraftEquippedWithProps {
  value: "all" | string[];
  onChange: (value: "all" | string[]) => void;
}

export function HireDraftEquippedWith({ value, onChange }: HireDraftEquippedWithProps) {
  const connectionsQ = useConnections();
  const connections = useMemo<Connection[]>(() => connectionsQ.data ?? [], [connectionsQ.data]);

  const allChecked = value === "all";
  const ids = allChecked ? connections.map((c) => c.connection_id) : (value as string[]);

  function toggleAll() {
    if (allChecked) onChange([]);
    else onChange("all");
  }

  function toggle(id: string) {
    if (allChecked) {
      onChange(connections.map((c) => c.connection_id).filter((cid) => cid !== id));
      return;
    }
    const cur = value as string[];
    onChange(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  return (
    <div style={wrap}>
      <div style={labelText}>Equipped with</div>

      {connections.length === 0 ? (
        <div style={emptyBox}>
          No tools connected.{" "}
          <Link to="/settings/endpoints" style={link}>
            Connect tools
          </Link>{" "}
          to equip your hires.
        </div>
      ) : (
        <div style={list}>
          <label style={row}>
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
            <span style={rowText}>All connections</span>
            <span style={rowMuted}>({connections.length})</span>
          </label>
          <div style={divider} />
          {connections.map((c) => {
            const checked = allChecked || ids.includes(c.connection_id);
            return (
              <label key={c.connection_id} style={row}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.connection_id)}
                />
                <span style={rowText}>{c.name}</span>
                <span style={rowMuted}>{c.tool_count} tools</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  paddingBottom: 10,
};

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  padding: 6,
  maxHeight: 160,
  overflowY: "auto",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 4px",
  fontSize: 13,
  color: "var(--color-text-primary)",
  cursor: "pointer",
};

const rowText: CSSProperties = {
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const rowMuted: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const divider: CSSProperties = {
  height: 1,
  background: "var(--color-border-secondary, var(--color-background-tertiary))",
  margin: "4px 0",
};

const emptyBox: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
  lineHeight: 1.5,
};

const link: CSSProperties = {
  color: "var(--color-text-info, var(--color-text-secondary))",
  textDecoration: "underline",
};
